import { useMutation, useQueryClient } from '@tanstack/react-query';

import { showToast } from '@/components/reusable/toast';
import {
	type IAddCommentRequest,
	type IBulkActionRequest,
	type IBulkActionResult,
	type ICreateEmuDeeTaskRequest,
	type ICreateFeedbackRequest,
	type IEmuDeeTaskResult,
	type IFeedbackAttachment,
	type IFeedbackComment,
	type IFeedbackSubmission,
	type IUpdateFeedbackRequest,
	type IVoteResult,
} from '@/interface/feedback';
import { feedbackApi } from '@/lib/api-client';

import { feedbackKeys } from './feedback-keys';

const apiErrorMessage = (error: unknown, fallback: string): string => {
	const data = (error as { response?: { data?: unknown } })?.response?.data;
	if (typeof data === 'string') return data;
	if (data && typeof data === 'object') {
		const obj = data as { message?: string; error?: { message?: string } };
		if (obj.error?.message) return obj.error.message;
		if (obj.message) return obj.message;
	}
	return fallback;
};

export const useCreateFeedback = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (data: ICreateFeedbackRequest) => {
			const { data: result } = await feedbackApi.post<IFeedbackSubmission>('/submissions', data);
			return result;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: feedbackKeys.all });
		},
	});
};

export const useUploadFeedbackAttachment = () =>
	useMutation({
		mutationFn: async ({ file, type }: { file: File; type: string }) => {
			const formData = new FormData();
			formData.append('file', file);
			const { data } = await feedbackApi.post<IFeedbackAttachment>(`/submissions/upload?type=${type}`, formData, {
				headers: { 'Content-Type': 'multipart/form-data' },
			});
			return data;
		},
	});

export const useUpdateFeedback = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ id, data }: { id: number; data: IUpdateFeedbackRequest }) => {
			const { data: result } = await feedbackApi.patch<IFeedbackSubmission>(`/submissions/${id}`, data);
			return result;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: feedbackKeys.all });
		},
	});
};

export const useUpdateFeedbackStatus = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ id, status }: { id: number; status: string }) => {
			const { data } = await feedbackApi.patch<IFeedbackSubmission>(`/submissions/${id}/status`, { status });
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: feedbackKeys.all });
		},
	});
};

export const useAddFeedbackComment = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ feedbackId, data }: { feedbackId: number; data: IAddCommentRequest }) => {
			const { data: result } = await feedbackApi.post<IFeedbackComment>(`/submissions/${feedbackId}/comments`, data);
			return result;
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: feedbackKeys.detail(variables.feedbackId) });
		},
	});
};

export const useVoteFeedback = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (id: number) => {
			const { data } = await feedbackApi.post<IVoteResult>(`/submissions/${id}/vote`);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: feedbackKeys.all });
		},
	});
};

export const useUnvoteFeedback = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (id: number) => {
			const { data } = await feedbackApi.delete<IVoteResult>(`/submissions/${id}/vote`);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: feedbackKeys.all });
		},
	});
};

export const useMergeFeedback = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ sourceId, targetId }: { sourceId: number; targetId: number }) => {
			const { data } = await feedbackApi.post<IFeedbackSubmission>(`/submissions/${sourceId}/merge`, { targetId });
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: feedbackKeys.all });
		},
	});
};

export const useBulkFeedbackAction = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (data: IBulkActionRequest) => {
			const { data: result } = await feedbackApi.post<IBulkActionResult>('/submissions/bulk', data);
			return result;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: feedbackKeys.all });
		},
	});
};

// Pushes a feedback item to the EmuDee task-management system via the backend
// proxy (api.feedback) — the live EmuDee key stays server-side.
export const useCreateEmuDeeTask = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ id, data }: { id: number; data: ICreateEmuDeeTaskRequest }) => {
			const { data: result } = await feedbackApi.post<IEmuDeeTaskResult>(`/submissions/${id}/emudee-task`, data);
			return result;
		},
		onSuccess: (result, variables) => {
			showToast('success', `Created ${result.displayId ?? 'task'} on EmuDee`);
			queryClient.invalidateQueries({ queryKey: feedbackKeys.detail(variables.id) });
			queryClient.invalidateQueries({ queryKey: feedbackKeys.lists() });
		},
		onError: (error) => {
			showToast('error', apiErrorMessage(error, 'Could not create the task on EmuDee.'));
		},
	});
};
